import { useState } from 'react';
import { SURFACES, type PersonUsage, type Surface, type UsageOverview } from '@/billing/api';
import { formatCopy } from '@/i18n/ns';
import { WorkspaceIcon } from '@/workspace/icons';
import { BarChart, type Series } from './bar-chart';
import { credits, shares, type Formatters } from './format';
import type { UsageCopy } from './copy';
import { PeopleList, type PersonView } from './people-list';
import { ServiceTable } from './service-table';

export const SERVICE_COLORS: Record<Surface, string> = {
  chat: 'var(--cu-svc-1)',
  agents: 'var(--cu-svc-2)',
  apps: 'var(--cu-svc-3)',
};

export const serviceSeries = (copy: UsageCopy): Series[] =>
  SURFACES.map((surface) => ({
    key: surface,
    label: copy[surface],
    color: SERVICE_COLORS[surface],
  }));

const surfaceValues = (overview: UsageOverview) =>
  overview.days.map((entry) => SURFACES.map((surface) => credits(entry[surface])));

function Legend({ series }: { series: Series[] }) {
  return (
    <div className="cu-legend">
      {series.map((item) => (
        <span className="cu-legend-item" key={item.key}>
          <span className="cu-key" style={{ background: item.color }} />
          {item.label}
        </span>
      ))}
    </div>
  );
}

export function ServiceCard({
  overview,
  days,
  today,
  outFrom,
  f,
  copy,
}: {
  overview: UsageOverview;
  days: string[];
  today: string;
  outFrom: number;
  f: Formatters;
  copy: UsageCopy;
}) {
  const [highlight, setHighlight] = useState(-1);
  const series = serviceSeries(copy);
  const values = surfaceValues(overview);
  const totals = SURFACES.map((_, j) => values.reduce((sum, row) => sum + row[j], 0));
  const total = totals.reduce((sum, value) => sum + value, 0);
  const share = shares(totals);
  const n = Math.max(1, days.length);
  return (
    <section className="cu-card" aria-label={copy.serviceTitle}>
      <div className="cu-card-head">
        <div>
          <h2 className="cu-card-title">{copy.serviceTitle}</h2>
          <p className="cu-card-sub">
            {overview.scope === 'company' ? copy.serviceSub : copy.serviceSubPersonal}
          </p>
        </div>
        <Legend series={series} />
      </div>
      {total > 0 ? (
        <BarChart
          series={series}
          days={days}
          values={values}
          today={today}
          outFrom={outFrom}
          highlight={highlight}
          f={f}
          copy={copy}
        />
      ) : (
        <div className="cu-empty">
          <strong>{copy.empty}</strong>
          <span>{copy.emptyBody}</span>
        </div>
      )}
      <ServiceTable
        rows={series.map((item, j) => ({
          key: item.key,
          label: item.label,
          color: item.color,
          credits: totals[j],
          share: share[j],
          avg: totals[j] / n,
        }))}
        total={total}
        avg={total / n}
        copy={copy}
        f={f}
        onHighlight={setHighlight}
      />
      {overview.unpricedEvents > 0 && (
        <p className="cu-unpriced">
          {formatCopy(copy.unpriced, { count: f.int(overview.unpricedEvents) })}
        </p>
      )}
    </section>
  );
}

export interface PeopleChart {
  series: Series[];
  values: number[][];
}

/** Per-person stacks for the top people, then everyone else, or one person's services. */
export function peopleChart({
  copy,
  views,
  companyDays,
  personDays,
  selected,
}: {
  copy: UsageCopy;
  views: PersonView[];
  companyDays: UsageOverview['days'];
  personDays: Map<string, UsageOverview['days']>;
  selected: PersonView | null;
}): PeopleChart | null {
  if (selected) {
    const days = personDays.get(selected.person.userId);
    if (!days) return null;
    return {
      series: serviceSeries(copy),
      values: days.map((entry) => SURFACES.map((surface) => credits(entry[surface]))),
    };
  }
  const top = views.filter((view) => view.person.creditMicros > 0).slice(0, 5);
  if (top.some((view) => !personDays.has(view.person.userId))) return null;
  const rest = views.length - top.length;
  const series: Series[] = top.map((view) => ({
    key: view.person.userId,
    label: view.person.name,
    color: view.color,
  }));
  if (rest > 0)
    series.push({
      key: 'everyone-else',
      label: formatCopy(copy.everyoneElse, { count: String(rest) }),
      color: 'var(--cu-u-other)',
    });
  const values = companyDays.map((entry, i) => {
    const total = SURFACES.reduce((sum, surface) => sum + credits(entry[surface]), 0);
    const own = top.map((view) => {
      const day = personDays.get(view.person.userId)?.[i];
      return day ? SURFACES.reduce((sum, surface) => sum + credits(day[surface]), 0) : 0;
    });
    const others = Math.max(0, total - own.reduce((sum, value) => sum + value, 0));
    return rest > 0 ? [...own, others] : own;
  });
  return { series, values };
}

export function PeopleCard({
  views,
  chart,
  days,
  today,
  outFrom,
  selected,
  editingId,
  currentUserId,
  canManage,
  f,
  copy,
  onSelect,
  onEdit,
  onSave,
  onCancel,
}: {
  views: PersonView[];
  chart: PeopleChart | null;
  days: string[];
  today: string;
  outFrom: number;
  selected: PersonView | null;
  editingId: string | null;
  currentUserId: string | null;
  canManage: boolean;
  f: Formatters;
  copy: UsageCopy;
  onSelect: (userId: string) => void;
  onEdit: (userId: string) => void;
  onSave: (person: PersonUsage, credits: number | null) => Promise<boolean>;
  onCancel: () => void;
}) {
  const top = views.filter((view) => view.person.creditMicros > 0).slice(0, 5).length;
  return (
    <section className="cu-card" aria-label={copy.userTitle}>
      <div className="cu-card-head">
        <div>
          <h2 className="cu-card-title">{copy.userTitle}</h2>
          {(selected || top > 0) && (
            <p className="cu-card-sub">
              {selected
                ? formatCopy(copy.userSubOne, { name: selected.person.name })
                : formatCopy(copy.userSub, { count: f.int(top) })}
            </p>
          )}
        </div>
        {chart && <Legend series={chart.series} />}
      </div>
      {chart ? (
        <BarChart
          series={chart.series}
          days={days}
          values={chart.values}
          today={today}
          outFrom={outFrom}
          f={f}
          copy={copy}
        />
      ) : (
        <div className="cu-chart-loading" role="status">
          <span className="ws-skeleton" />
        </div>
      )}
      <PeopleList
        views={views}
        currentUserId={currentUserId}
        selectedId={selected?.person.userId ?? null}
        editingId={editingId}
        canManage={canManage}
        copy={copy}
        f={f}
        onSelect={onSelect}
        onEdit={onEdit}
        onSave={onSave}
        onCancel={onCancel}
      />
      <div className="cu-note">
        <WorkspaceIcon name="alert" size={15} />
        <div>
          <p>{copy.noteLimits}</p>
          <p>{copy.noteAdmin}</p>
        </div>
      </div>
    </section>
  );
}
